import { ref, watch } from 'vue';

/**
 * Which columns a track-list EXPORT contains, for the card-based track lists
 * (a bucket's Tracks tab, a custom list, a generated playlist).
 *
 * Deliberately its own module-level preference rather than a scope in
 * `persistentTableStore`. That store resolves its scope through
 * `resolvePageLevelEntityId(routeName)`, so a pseudo-entity has to be claimed by
 * a ROUTE — and these card lists are not a route. They render inside the
 * inspector overlay, pushed from the Search page (`PlaylistContainerInspector`
 * and `LiveTrackUsageInspectorContent`) and from the player card on any route.
 * Claiming `search` would repoint the Search results table's own column state;
 * claiming `playlist-switching` would displace the entity that page owns. There
 * is no route that can be claimed without breaking the page it was claimed from.
 *
 * So this copies `trackCardDensity.ts` — the established idiom for "one
 * preference shared by every open track list", with the same localStorage
 * handling. The Search page keeps using its real Column Settings; only the card
 * lists need this.
 *
 * Scope note: this drives the EXPORT only, not what the cards render.
 * `TrackCard` is three separately written layouts and a field exists only in the
 * layouts with a slot for it (ISRC and bitrate are `full`-only), so a picker
 * layered on top could subtract from the active density but never add to it —
 * half its toggles would silently do nothing. The control is labelled "Export
 * columns" to say exactly what it governs.
 */

/** Column ids offered for a card-list export, matching the backend's card vocabulary. */
export const TRACK_LIST_COLUMNS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'position', label: '#' },
  { id: 'id', label: 'Live Track ID' },
  { id: 'code', label: 'Live Track Code' },
  { id: 'name', label: 'Title' },
  { id: 'remix_name', label: 'Remix' },
  { id: 'artist_name', label: 'Artist' },
  { id: 'album_name', label: 'Album' },
  { id: 'genre_name', label: 'Genre' },
  { id: 'rating', label: 'Rating' },
  { id: 'playlist_count', label: 'Playlist Count' },
  { id: 'isrc', label: 'ISRC' },
  { id: 'bitrate', label: 'Bitrate' },
  { id: 'duration', label: 'Duration (s)' },
  { id: 'copyright', label: '©Year' },
  { id: 'uploaded_date', label: 'Uploaded' },
  { id: 'created_at', label: 'Created' },
  { id: 'tags', label: 'Tags' },
] as const;

const VALID_COLUMN_IDS = new Set(TRACK_LIST_COLUMNS.map((column) => column.id));

/** What a first-time user gets: the identity of a track, plus where it sits. */
export const DEFAULT_TRACK_LIST_COLUMN_IDS: string[] = [
  'position',
  'code',
  'name',
  'remix_name',
  'artist_name',
  'album_name',
  'duration',
];

const STORAGE_KEY = 'track-list-export-columns:v1';

function readPersistedColumnIds(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [...DEFAULT_TRACK_LIST_COLUMN_IDS];
    }

    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return [...DEFAULT_TRACK_LIST_COLUMN_IDS];
    }

    // Drop anything that is no longer a real column, so a stale preference
    // degrades to a thinner picker rather than requesting columns the backend
    // will silently discard.
    const valid = parsed.filter(
      (columnId): columnId is string => typeof columnId === 'string' && VALID_COLUMN_IDS.has(columnId),
    );

    return valid.length > 0 ? valid : [...DEFAULT_TRACK_LIST_COLUMN_IDS];
  } catch {
    return [...DEFAULT_TRACK_LIST_COLUMN_IDS];
  }
}

const selectedColumnIds = ref<string[]>(readPersistedColumnIds());

watch(
  selectedColumnIds,
  (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      // Private mode / no storage — keep the in-memory choice, just don't persist.
    }
  },
  { deep: true },
);

export function useTrackListColumns() {
  return selectedColumnIds;
}

/** Test hook: restore defaults without touching localStorage semantics. */
export function resetTrackListColumnsForTesting(): void {
  selectedColumnIds.value = [...DEFAULT_TRACK_LIST_COLUMN_IDS];
}
